import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Settings,
  Shield,
  Clock,
  Landmark,
  Bell,
  Sliders,
  Plus,
  Mail,
  Check,
  ChevronDown,
  Save,
} from "lucide-react";

const tabs = [
  "Overview",
  "Timestamp Records",
  "Verification",
  "Time Sources",
  "Authorities",
  "Settings",
];

// Reusable Switch Component for pixel-perfect toggle UI
const ToggleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
      checked ? "bg-indigo-600" : "bg-slate-300"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? "translate-x-4" : "translate-x-0"
      }`}
    />
  </button>
);

export default function TimeSettings() {
  const navigate = useNavigate();

  const [generalSettings, setGeneralSettings] = useState({
    serviceName: "I-PQSign Timestamp Service",
    dateFormat: "DD/MM/YYYY",
    hashAlgorithm: "SHA-256",
    defaultTimezone: "(UTC+05:30) India Standard Time",
    timeFormat: "24 Hour (HH:mm:ss)",
    defaultCertPolicy: "RFC 3161",
  });

  const [securitySettings, setSecuritySettings] = useState({
    hsmIntegration: true,
    hsmStatus: "Thales HSM",
    multiFactorAuth: true,
    auditLogging: true,
    trustedIpsOnly: false,
    keyRotationInterval: "90 Days",
  });

  const [emailNotifications, setEmailNotifications] = useState({
    success: true,
    verification: true,
    expiry: true,
    maintenance: true,
  });

  const [notificationRecipients, setNotificationRecipients] = useState({
    adminEmail: "admin@acme.com",
    securityEmail: "security@acme.com",
    operationsEmail: "ops@acme.com",
  });

  const [advancedSettings, setAdvancedSettings] = useState({
    algorithm: "SHA-256",
    maxAttempts: "3",
    requestTimeout: "30",
    logLevel: "Info",
    proxyConfig: "None",
    customEndpoint: "https://custom.tsa.intrust",
  });

  const timeSources = [
    {
      id: 1,
      name: "GlobalSign TSA",
      type: "Public TSA",
      url: "https://timestamp.globalsign.com",
      status: "Active",
      lastSync: "May 31, 2025 10:30 AM",
    },
    {
      id: 2,
      name: "Sectigo TSA",
      type: "Public TSA",
      url: "https://timestamp.sectigo.com",
      status: "Active",
      lastSync: "May 31, 2025 10:29 AM",
    },
    {
      id: 3,
      name: "DigiCert TSA",
      type: "Public TSA",
      url: "https://timestamp.digicert.com",
      status: "Active",
      lastSync: "May 31, 2025 10:30 AM",
    },
    {
      id: 4,
      name: "I-PQSign TSA (Internal)",
      type: "Internal TSA",
      url: "https://internal.tsa.intrust",
      status: "Active",
      lastSync: "May 31, 2025 10:28 AM",
    },
    {
      id: 5,
      name: "NIST Time Server",
      type: "Public NTP",
      url: "time.nist.gov",
      status: "Active",
      lastSync: "May 31, 2025 10:28 AM",
    },
  ];

  const authorities = [
    {
      id: 1,
      name: "GlobalSign TSA",
      country: "USA",
      flag: "🇺🇸",
      type: "Public TSA",
      status: "Active",
      expiryDate: "May 31, 2026",
    },
    {
      id: 2,
      name: "Sectigo TSA",
      country: "USA",
      flag: "🇺🇸",
      type: "Public TSA",
      status: "Active",
      expiryDate: "May 31, 2026",
    },
    {
      id: 3,
      name: "DigiCert TSA",
      country: "USA",
      flag: "🇺🇸",
      type: "Public TSA",
      status: "Active",
      expiryDate: "May 31, 2026",
    },
    {
      id: 4,
      name: "I-PQSign TSA",
      country: "IND",
      flag: "🇮🇳",
      type: "Internal TSA",
      status: "Active",
      expiryDate: "May 31, 2026",
    },
    {
      id: 5,
      name: "NIST",
      country: "USA",
      flag: "🇺🇸",
      type: "Public NTP",
      status: "Active",
      expiryDate: "May 31, 2026",
    },
  ];

  const handleGeneralChange = (field, value) => {
    setGeneralSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSecurityToggle = (field) => {
    setSecuritySettings((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleNotificationToggle = (field) => {
    setEmailNotifications((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleRecipientChange = (field, value) => {
    setNotificationRecipients((prev) => ({ ...prev, [field]: value }));
  };

  const handleAdvancedChange = (field, value) => {
    setAdvancedSettings((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800 pb-10 -mx-6 -mt-6">
      {/* Top Header & Navigation */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-2.5">
          <nav className="flex gap-8">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  if (tab === "Settings") return;
                  if (tab === "Overview") navigate("/timestamps");
                  else if (tab === "Timestamp Records") navigate("/timestamps/records");
                  else if (tab === "Verification") navigate("/timestamps/verification");
                  else if (tab === "Time Sources") navigate("/timestamps/TimeSource");
                  else if (tab === "Authorities") navigate("/timestamps/authorities");
                }}
                className={`py-1 text-xs font-medium transition-colors relative ${
                  tab === "Settings"
                    ? "text-indigo-600 font-semibold after:absolute after:bottom-[-10px] after:left-0 after:right-0 after:h-[2px] after:bg-indigo-600"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>

          <button className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700">
            <Plus className="h-3.5 w-3.5" /> Save Settings
          </button>
        </div>
      </header>

      {/* Main Grid Content Layout */}
      <main className="mx-auto max-w-[1600px] px-6 pt-4">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          
          {/* CARD 1: General Settings */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
                  <Settings className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900">General Settings</h2>
                  <p className="text-xs text-slate-500">Configure basic settings for timestamping service.</p>
                </div>
              </div>
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-600 border border-emerald-200/50">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Service Name</label>
                <input
                  type="text"
                  value={generalSettings.serviceName}
                  onChange={(e) => handleGeneralChange("serviceName", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none"
                />
              </div>

              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Default Timezone</label>
                <select
                  value={generalSettings.defaultTimezone}
                  onChange={(e) => handleGeneralChange("defaultTimezone", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                >
                  <option>(UTC+05:30) India Standard Time</option>
                  <option>UTC Coordinated Universal Time</option>
                  <option>UTC-05:00 Eastern Time</option>
                </select>
              </div>

              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Date Format</label>
                <select
                  value={generalSettings.dateFormat}
                  onChange={(e) => handleGeneralChange("dateFormat", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                >
                  <option>DD/MM/YYYY</option>
                  <option>MM/DD/YYYY</option>
                  <option>YYYY-MM-DD</option>
                </select>
              </div>

              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Time Format</label>
                <select
                  value={generalSettings.timeFormat}
                  onChange={(e) => handleGeneralChange("timeFormat", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                >
                  <option>24 Hour (HH:mm:ss)</option>
                  <option>12 Hour (HH:mm:ss AM/PM)</option>
                </select>
              </div>

              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Hash Algorithm</label>
                <select
                  value={generalSettings.hashAlgorithm}
                  onChange={(e) => handleGeneralChange("hashAlgorithm", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                >
                  <option>SHA-256</option>
                  <option>SHA-384</option>
                  <option>SHA-512</option>
                </select>
              </div>

              <div>
                <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Default Certificate Policy</label>
                <select
                  value={generalSettings.defaultCertPolicy}
                  onChange={(e) => handleGeneralChange("defaultCertPolicy", e.target.value)}
                  className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                >
                  <option>RFC 3161</option>
                  <option>RFC 3339</option>
                </select>
              </div>
            </div>
          </div>

          {/* CARD 2: Security Settings */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-3 border-b border-slate-100 pb-2">
              <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
                <Shield className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-900">Security Settings</h2>
                <p className="text-xs text-slate-500">Configure security options for a trusted environment.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* Left Column Controls */}
              <div className="space-y-2">
                <div>
                  <div className="flex items-center justify-between mb-0.5">
                    <label className="text-[11px] font-medium text-slate-600">HSM Integration</label>
                    <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 border border-emerald-200/50">
                      <Check className="h-3 w-3" /> Connected
                    </span>
                  </div>
                  <select
                    value={securitySettings.hsmStatus}
                    onChange={(e) => setSecuritySettings((p) => ({ ...p, hsmStatus: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                  >
                    <option>Thales HSM</option>
                    <option>AWS CloudHSM</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-0.5 text-[11px] font-medium text-slate-600">Key Rotation Interval</label>
                  <select
                    value={securitySettings.keyRotationInterval}
                    onChange={(e) => setSecuritySettings((p) => ({ ...p, keyRotationInterval: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-xs focus:border-indigo-600 focus:outline-none bg-white"
                  >
                    <option>90 Days</option>
                    <option>60 Days</option>
                    <option>30 Days</option>
                  </select>
                </div>
              </div>

             {/* Right Column Switches */}
<div className="space-y-3 md:border-l md:border-slate-100 md:pl-4">
  <div className="flex items-start gap-3">
    <ToggleSwitch
      checked={securitySettings.multiFactorAuth}
      onChange={() => handleSecurityToggle("multiFactorAuth")}
    />
    <div>
      <p className="text-xs font-bold text-slate-800">
        Enforce Multi-Factor Authentication
      </p>
      <p className="text-[11px] text-slate-400 mt-0.5">
        Require MFA for all administrative actions.
      </p>
    </div>
  </div>

  <div className="flex items-start gap-3">
    <ToggleSwitch
      checked={securitySettings.auditLogging}
      onChange={() => handleSecurityToggle("auditLogging")}
    />
    <div>
      <p className="text-xs font-bold text-slate-800">
        Enable Audit Logging
      </p>
      <p className="text-[11px] text-slate-400 mt-0.5">
        Log all timestamp related activities.
      </p>
    </div>
  </div>

  <div className="flex items-start gap-3">
    <ToggleSwitch
      checked={securitySettings.trustedIpsOnly}
      onChange={() => handleSecurityToggle("trustedIpsOnly")}
    />
    <div>
      <p className="text-xs font-bold text-slate-800">
        Allow Trusted IPs Only
      </p>
      <p className="text-[11px] text-slate-400 mt-0.5">
        Restrict access to trusted IP addresses.
      </p>
    </div>
  </div>
</div>
            </div>
          </div>

          {/* CARD 3: Time Source Settings */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Time Source Settings</h2>
                  <p className="text-xs text-slate-500">Configure time sources for accurate and reliable timestamping</p>
                </div>
              </div>
              <button className="flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700">
                <Plus className="h-3 w-3" /> Add Time Source
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="py-1.5 font-medium">Source Name</th>
                    <th className="py-1.5 font-medium">Type</th>
                    <th className="py-1.5 font-medium">URL / Endpoint</th>
                    <th className="py-1.5 font-medium">Status</th>
                    <th className="py-1.5 font-medium">Last Sync</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {timeSources.map((source) => (
                    <tr key={source.id} className="hover:bg-slate-50/50">
                      <td className="py-2 font-medium text-slate-800">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-indigo-600 inline-block flex-shrink-0" />
                          {source.name}
                        </div>
                      </td>
                      <td className="py-2 text-slate-600">{source.type}</td>
                      <td className="py-2 text-indigo-600 hover:underline cursor-pointer">{source.url}</td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 border border-emerald-100">
                          <span className="h-1 w-1 rounded-full bg-emerald-500" /> {source.status}
                        </span>
                      </td>
                      <td className="py-2 text-slate-500">{source.lastSync}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CARD 4: Authorities Management */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
                  <Landmark className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-slate-900">Authorities Management</h2>
                  <p className="text-xs text-slate-500">Manage trusted timestamp authorities and certificates</p>
                </div>
              </div>
              <button className="flex items-center gap-1 rounded-md bg-indigo-600 px-2.5 py-1 text-[11px] font-medium text-white hover:bg-indigo-700">
                <Plus className="h-3 w-3" /> Add Authority
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-[11px]">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-500">
                    <th className="py-1.5 font-medium">Authority Name</th>
                    <th className="py-1.5 font-medium">Country</th>
                    <th className="py-1.5 font-medium">Type</th>
                    <th className="py-1.5 font-medium">Status</th>
                    <th className="py-1.5 font-medium">Expiry Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {authorities.map((auth) => (
                    <tr key={auth.id} className="hover:bg-slate-50/50">
                      <td className="py-2 font-medium text-slate-800">
                        <div className="flex items-center gap-1.5">
                          <span className="h-2 w-2 rounded-full bg-indigo-600 inline-block flex-shrink-0" />
                          {auth.name}
                        </div>
                      </td>
                      <td className="py-2 text-slate-600">
                        <span className="mr-1">{auth.flag}</span>{auth.country}
                      </td>
                      <td className="py-2 text-slate-600">{auth.type}</td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600 border border-emerald-100">
                          <span className="h-1 w-1 rounded-full bg-emerald-500" /> {auth.status}
                        </span>
                      </td>
                      <td className="py-2 text-slate-500">{auth.expiryDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CARD 5: Notifications Settings */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
            <div className="mb-2 flex items-center gap-3 pb-2">
              <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
                <Bell className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-sm font-bold text-slate-900">Notifications Settings</h2>
                <p className="text-xs text-slate-500">Configure email and system notifications.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {/* Left Column: Email Notifications List */}
              <div>
                <h3 className="mb-1 text-xs font-bold text-slate-800">Email Notifications</h3>
                <div className="space-y-1.5">
                  <div className="flex items-start gap-3">
                    <ToggleSwitch checked={emailNotifications.success} onChange={() => handleNotificationToggle("success")} />
                    <div>
                      <p className="text-[11px] font-medium text-slate-800">Timestamp Success Notifications</p>
                      <p className="text-[10px] text-slate-400">Notify when timestamp is successfully created.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ToggleSwitch checked={emailNotifications.verification} onChange={() => handleNotificationToggle("verification")} />
                    <div>
                      <p className="text-[11px] font-medium text-slate-800">Verification Alerts</p>
                      <p className="text-[10px] text-slate-400">Notify on verification status changes.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ToggleSwitch checked={emailNotifications.expiry} onChange={() => handleNotificationToggle("expiry")} />
                    <div>
                      <p className="text-[11px] font-medium text-slate-800">Certificate Expiry Alerts</p>
                      <p className="text-[10px] text-slate-400">Notify before certificate expiry.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <ToggleSwitch checked={emailNotifications.maintenance} onChange={() => handleNotificationToggle("maintenance")} />
                    <div>
                      <p className="text-[11px] font-medium text-slate-800">System Maintenance Alerts</p>
                      <p className="text-[10px] text-slate-400">Notify about scheduled maintenance.</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Notification Recipients Form */}
              <div>
                <h3 className="mb-1 text-xs font-bold text-slate-800">Notification Recipients</h3>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2.5">
                    <label className="text-[11px] font-medium text-slate-600 shrink-0 w-[120px]">Admin Email</label>
                    <input
                      type="email"
                      value={notificationRecipients.adminEmail}
                      onChange={(e) => handleRecipientChange("adminEmail", e.target.value)}
                      className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] focus:border-indigo-600 focus:outline-none"
                      style={{ width: `${notificationRecipients.adminEmail.length + 4}ch` }}
                    />
                  </div>
                  <div className="flex items-center gap-2.5">
                    <label className="text-[11px] font-medium text-slate-600 shrink-0 w-[120px]">Security Team Email</label>
                    <input
                      type="email"
                      value={notificationRecipients.securityEmail}
                      onChange={(e) => handleRecipientChange("securityEmail", e.target.value)}
                      className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] focus:border-indigo-600 focus:outline-none"
                      style={{ width: `${notificationRecipients.securityEmail.length + 4}ch` }}
                    />
                  </div>
                  <div className="flex items-center gap-2.5">
                    <label className="text-[11px] font-medium text-slate-600 shrink-0 w-[120px]">Operations Email</label>
                    <input
                      type="email"
                      value={notificationRecipients.operationsEmail}
                      onChange={(e) => handleRecipientChange("operationsEmail", e.target.value)}
                      className="rounded-md border border-slate-200 px-2.5 py-1.5 text-[11px] focus:border-indigo-600 focus:outline-none"
                      style={{ width: `${notificationRecipients.operationsEmail.length + 4}ch` }}
                    />
                  </div>
                  <button className="mt-1 flex items-center gap-1.5 rounded-md border border-violet-400 px-3 py-1.5 text-[11px] font-medium text-violet-600 hover:bg-violet-50">
                    <Mail className="h-3 w-3 text-violet-500" /> Test Notifications
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* CARD 6: Advanced Settings */}
<div className="w-full rounded-2xl border border-slate-200/80 bg-white p-3 shadow-xs font-sans">
  {/* Header Section */}
  <div className="mb-2 flex items-center gap-3">
    <div className="rounded-full bg-indigo-50 p-3 text-indigo-600">
      <Sliders className="h-6 w-6" />
    </div>
    <div>
      <h2 className="text-sm font-bold text-slate-900">Advanced Settings</h2>
      <p className="text-xs text-slate-500">Advanced configuration options for the timestamp service.</p>
    </div>
  </div>

  {/* Top Controls Grid */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
    {/* Timestamp Algorithm */}
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        Timestamp Algorithm
      </label>
      <div className="relative">
        <select
          value={advancedSettings.algorithm}
          onChange={(e) => handleAdvancedChange("algorithm", e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-800 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
        >
          <option>SHA-256</option>
          <option>SHA-384</option>
          <option>SHA-512</option>
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </div>

    {/* Retry Attempts */}
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        Retry Attempts
      </label>
      <div className="relative">
        <select
          value={advancedSettings.maxAttempts}
          onChange={(e) => handleAdvancedChange("maxAttempts", e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-800 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
        >
          <option>3</option>
          <option>5</option>
          <option>10</option>
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </div>

    {/* Request Timeout */}
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        Request Timeout <span className="text-slate-400 font-normal">(seconds)</span>
      </label>
      <input
        type="text"
        value={advancedSettings.requestTimeout}
        onChange={(e) => handleAdvancedChange("requestTimeout", e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-800 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      />
    </div>

    {/* Log Level */}
    <div>
      <label className="block text-xs font-semibold text-slate-700 mb-1.5">
        Log Level
      </label>
      <div className="relative">
        <select
          value={advancedSettings.logLevel}
          onChange={(e) => handleAdvancedChange("logLevel", e.target.value)}
          className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-800 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
        >
          <option>Info</option>
          <option>Debug</option>
          <option>Warn</option>
          <option>Error</option>
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      </div>
    </div>
  </div>

  {/* Network Settings Section */}
  <div>
    <h3 className="text-xs font-bold text-slate-900 mb-2">
      Network Settings
    </h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
      {/* Proxy Configuration */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
          Proxy Configuration
        </label>
        <div className="relative">
          <select
            value={advancedSettings.proxyConfig}
            onChange={(e) => handleAdvancedChange("proxyConfig", e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-800 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 cursor-pointer"
          >
            <option>None</option>
            <option>HTTP Proxy</option>
            <option>SOCKS5</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
      </div>

      {/* Custom Endpoint */}
      <div>
        <label className="block text-xs font-semibold text-slate-700 mb-1.5">
          Custom Endpoint <span className="text-slate-400 font-normal">(Optional)</span>
        </label>
        <input
          type="text"
          placeholder="https://custom.tsa.intrust"
          value={advancedSettings.customEndpoint}
          onChange={(e) => handleAdvancedChange("customEndpoint", e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-xs font-medium text-slate-400 shadow-xs focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder:text-slate-300"
        />
      </div>

      {/* Save Button Aligned Right */}
      <div className="lg:col-span-2 flex justify-end">
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-xs font-semibold text-white shadow-xs transition hover:bg-indigo-700 cursor-pointer"
        >
          <Save className="h-4 w-4 stroke-[2]" /> Save Advanced Settings
        </button>
      </div>
    </div>
  </div>
</div>

        </div>
      </main>
    </div>
  );
}